import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
	private readonly USER_KEY = 'currentUser';

	private currentUserSubject = new BehaviorSubject<any>(this.getCurrentUser());
	public currentUser$: Observable<any> = this.currentUserSubject.asObservable();

	// Event for profile photo updates
	private profilePhotoUpdatedSubject = new Subject<void>();
	public profilePhotoUpdated$ = this.profilePhotoUpdatedSubject.asObservable();

	/**
	 * Returns the active user.
	 */
	getCurrentUser(): any {
		const userStr = localStorage.getItem(this.USER_KEY);
		if (!userStr) return null;

		try {
			const user = JSON.parse(userStr);
			return user;
		} catch (error) {
			console.error('Error parsing user data:', error);
			return null;
		}
	}

	/**
	 * Returns the Supabase ID of the active user.
	 */
	getId(): string | undefined {
		const user = this.getCurrentUser();
		return user?.id;
	}

	/**
	 * Returns the database user ID of the active user.
	 */
	getUserId(): number | undefined {
		const user = this.getCurrentUser();
		return user?.userId;
	}

	/**
	 * Returns the general email of the active user.
	 */
	getEmail(): string | undefined {
		const user = this.getCurrentUser();
		return user?.email;
	}

	/**
	 * Updates the active user.
	 */
	setCurrentUser(userData: any): void {
		// Ensure we have the minimum required data
		const user = {
			id: userData.id, // Supabase ID
			userId: userData.userId, // Database ID
			email: userData.email,
			nombre: userData.nombre,
			tipo: userData.tipo,
			role: userData.role || null, // Single role object
			permissions: userData.permissions || [], // Array of permission names
			enabled: userData.enabled,
			emailVerified: userData.emailVerified,
			profilePhotoUrl: userData.profilePhotoUrl || null, // Cached profile photo URL
			preferredLanguage: userData.preferredLanguage || 'en', // User's preferred language
		};

		localStorage.setItem(this.USER_KEY, JSON.stringify(user));
		this.currentUserSubject.next(user);
	}

	/**
	 * Returns the profile photo URL of the active user.
	 */
	getProfilePhotoUrl(): string | null {
		const user = this.getCurrentUser();
		return user?.profilePhotoUrl || null;
	}

	/**
	 * Updates only the profile photo URL without affecting other user data.
	 */
	updateProfilePhotoUrl(url: string | null): void {
		const user = this.getCurrentUser();
		if (user) {
			user.profilePhotoUrl = url;
			localStorage.setItem(this.USER_KEY, JSON.stringify(user));
			this.currentUserSubject.next(user);
			// Emit event for profile photo update
			this.profilePhotoUpdatedSubject.next();
		}
	}

	/**
	 * Updates only the preferred language without affecting other user data.
	 */
	updatePreferredLanguage(lang: string): void {
		const user = this.getCurrentUser();
		if (user) {
			user.preferredLanguage = lang;
			localStorage.setItem(this.USER_KEY, JSON.stringify(user));
			this.currentUserSubject.next(user);
		}
	}

	/**
	 * Returns the preferred language of the active user.
	 */
	getPreferredLanguage(): string | null {
		const user = this.getCurrentUser();
		return user?.preferredLanguage || null;
	}

	/**
	 * Clears the active user from localStorage (logout).
	 */
	clearCurrentUser(): void {
		localStorage.removeItem(this.USER_KEY);
		this.currentUserSubject.next(null);
	}

	/**
	 * Returns the role of the active user.
	 */
	getUserRole(): { id: string; name: string; description?: string } | null {
		const user = this.getCurrentUser();
		return user?.role || null;
	}

	/**
	 * Returns the role name of the active user.
	 */
	getUserRoleName(): string | null {
		const user = this.getCurrentUser();
		return user?.role?.name || null;
	}

	/**
	 * Returns all roles of the active user (as an array for compatibility).
	 */
	getUserRoles(): string[] {
		const roleName = this.getUserRoleName();
		return roleName ? [roleName] : [];
	}

	/**
	 * Checks if the user has a specific role
	 */
	hasRole(roleToCheck: string): boolean {
		const user = this.getCurrentUser();
		return user?.role?.name?.toLowerCase() === roleToCheck.toLowerCase() || false;
	}

	/**
	 * Checks if the user has any of the specified roles
	 */
	hasAnyRole(rolesToCheck: string[]): boolean {
		const user = this.getCurrentUser();
		const userRole = user?.role?.name?.toLowerCase();
		return rolesToCheck.some((role) => role.toLowerCase() === userRole) || false;
	}

	/**
	 * Returns the user's base type (always employee)
	 */
	getBaseType(): 'employee' | null {
		return 'employee';
	}

	/**
	 * Returns all permissions of the active user
	 */
	getPermissions(): string[] {
		const user = this.getCurrentUser();
		return user?.permissions || [];
	}

	/**
	 * Checks if the user has a specific permission
	 */
	hasPermission(permissionToCheck: string): boolean {
		const permissions = this.getPermissions();
		return permissions.includes(permissionToCheck);
	}

	/**
	 * Checks if the user has any of the specified permissions
	 */
	hasAnyPermission(permissionsToCheck: string[]): boolean {
		const userPermissions = this.getPermissions();
		return permissionsToCheck.some((permission) => userPermissions.includes(permission));
	}

	/**
	 * Checks if the user has all of the specified permissions
	 */
	hasAllPermissions(permissionsToCheck: string[]): boolean {
		const userPermissions = this.getPermissions();
		return permissionsToCheck.every((permission) => userPermissions.includes(permission));
	}
}
